import type { DisplayMode, FilterFunction } from "@common/utils/todo-utils";
import {
  isDateBasedGroup,
  isSameDay,
  WEEK_FORECAST_DAYS,
} from "@common/utils/todo-utils";
import type { Moment } from "moment";
import moment from "moment";
import type { GroupKey, Todo, TodoUpdateDelta, TodoIdMap } from "../types/todo";
import { isHashtagMetaItem } from "../types/todo";
import TodoUtils from "../utils/todo-utils";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";

export const PRESET_GROUP_KEYS = {
  TODAY: "today",
  BACKLOG: "backlog",
  // Default View
  UPCOMING: "upcoming",
  // Week View
  SOMEDAY: "someday",
};

export type TodoListByGroup = { [groupKey: GroupKey]: Todo[] };

/**
 * Get all todos grouped as an UNORDERED list by group key
 */
function _getTodosByGroup(
  todoIdMap: TodoIdMap,
  mode: DisplayMode,
  options?: {
    outermost: boolean;
    parentChildMap?: ReturnType<typeof TodoUtils.parentChildMap>;
  },
) {
  const parentChildMap =
    options?.parentChildMap ??
    TodoUtils.parentChildMap(Object.values(todoIdMap));
  const todosByGroupSet: { [groupKey: GroupKey]: Set<Todo> } = {};
  const allTodos = Object.values(todoIdMap);
  const todosOrOutermostTodos = options?.outermost
    ? allTodos.filter((todo) => todo.parent === null)
    : allTodos;

  todosOrOutermostTodos.forEach((todo) => {
    const groupKeys = groupKeysForTodo(todo, mode, todoIdMap, parentChildMap);
    groupKeys.forEach((groupKey) => {
      todosByGroupSet[groupKey] ??= new Set<Todo>();
      todosByGroupSet[groupKey].add(todo);
    });
  });

  return Object.entries(todosByGroupSet).reduce((acc, [groupKey, todos]) => {
    acc[groupKey] = [...todos];
    return acc;
  }, {} as TodoListByGroup);
}

function todoListByGroup(
  todoIdMap: TodoIdMap,
  mode: DisplayMode,
  filterFn: FilterFunction,
  includeCompleted: boolean = false,
  sortAsc: boolean = true,
): TodoListByGroup {
  //1. get outermost todos
  let allTodosList = Object.values(
    TodoUtils.withoutDeleted(todoIdMap),
  ) as Todo[];
  //map children to parents
  let childrenMap = TodoUtils.parentChildMap(allTodosList);
  let outermostTodos = allTodosList.filter((todo) => {
    return todo.parent == null || !childrenMap[todo.parent];
  });

  //2. bin outermost todos by day
  let outermostTodosByGroup = _getTodosByGroup(todoIdMap, mode, {
    outermost: true,
  });

  //3. traverse dfs to filter
  const childPassFilterMemo: Record<string, boolean> = {};
  function hasChildPassPositiveFilterCondition(todo: Todo): boolean {
    // returned memoised value if exists
    if (childPassFilterMemo[todo.id] !== undefined) {
      return childPassFilterMemo[todo.id]!;
    }

    const children: Todo[] | undefined = childrenMap[todo.id];

    // if is leaf, self is truth
    const { passPositiveFilter } = filterFn(todo);
    if (!children?.length || passPositiveFilter) {
      childPassFilterMemo[todo.id] = passPositiveFilter;
      return passPositiveFilter;
    }

    // if has children, as long as one child has, return true (recursive)
    for (let child of children) {
      if (hasChildPassPositiveFilterCondition(child)) {
        childPassFilterMemo[todo.id] = true;
        return true;
      }
    }

    // none of children pass
    childPassFilterMemo[todo.id] = false;
    return false;
  }

  //3. traverse dfs to get daily list
  let flattenedTodosByGroup: TodoListByGroup = {};
  for (let [key, todos] of Object.entries(outermostTodosByGroup)) {
    let finalFlattenedTodos = [];
    let sortedOutermost = TodoUtils.sortedTodos(todos, sortAsc);
    //inject indentation
    sortedOutermost = sortedOutermost.map((todo) => ({ ...todo, indent: 0 }));
    let queue = [...sortedOutermost];
    // lowest (closest to root) level that has a todo that passes the filter
    let lowestSelfPassLevel = -1;
    while (queue.length != 0) {
      let todo = queue.shift() as Todo;
      let thisLevel = todo.indent;

      // reset self pass, as it has finished its children
      if (thisLevel <= lowestSelfPassLevel) {
        lowestSelfPassLevel = -1;
      }

      /**
       * in positive filters:
       * - optimistic: if there is at least one in the tree, show result
       * - positive filters trickle down (if parent passes, children pass too)
       *
       * in anti filters: (let's call this negative filters)
       * - pessimistic: if there is at least one in the tree, hide result
       * - negative filters trickle down (if parent fails, children fail too)
       */
      const { passPositiveFilter, passNegativeFilter, hasPositiveFilter } =
        filterFn(todo);
      const hasChildThatPassesPositivefilter =
        hasChildPassPositiveFilterCondition(todo);
      // if there is a postive filter AND a negative filter,
      // but there is a child that fits the positive filter
      // we still want to show that child
      // so, this should only skip if there is no positive filter child matches
      if (
        !passNegativeFilter &&
        !(hasPositiveFilter && hasChildThatPassesPositivefilter)
      ) {
        // skip this todo, and effectively its children
        continue;
      }

      if (passPositiveFilter && lowestSelfPassLevel == -1) {
        lowestSelfPassLevel = thisLevel;
      }

      const parentPassedPositive =
        thisLevel > lowestSelfPassLevel && lowestSelfPassLevel != -1;
      const isRootOfCompleteBranch =
        todo.parent == null && TodoUtils.isBranchComplete(todo.id, todoIdMap); // completed branches should be excluded
      if (isRootOfCompleteBranch && !includeCompleted) {
        // skip this todo, and effectively its children
        continue;
      }

      if (parentPassedPositive || hasChildThatPassesPositivefilter) {
        finalFlattenedTodos.push(todo);

        if (!todo.collapsed) {
          let children = TodoUtils.sortedTodos(childrenMap[todo.id] || []);
          //inject indentation
          children = children.map((eachTodo) => ({
            ...eachTodo,
            indent: todo.indent + 1,
          }));

          // if self passes, children get free pass
          queue = [...children, ...queue]; //insert children at front - dfs
        }
      }
    }

    flattenedTodosByGroup[key] = finalFlattenedTodos;
  }

  return flattenedTodosByGroup;
}

function todoListForGroup(
  groupKey: GroupKey,
  todoIdMap: TodoIdMap,
  filterFn: FilterFunction,
  displayMode: DisplayMode,
  includeCompleted = false,
) {
  // Prepare for display:
  // 1. filter out deleted
  // 2. filter out based on filterFn
  // 3. filter out completed branches

  // 1. get outermost todos
  const allTodosList = Object.values<Todo>(TodoUtils.withoutDeleted(todoIdMap));
  const parentChildMapCache = TodoUtils.parentChildMap(allTodosList);
  // map children to parents
  const childrenMap = TodoUtils.parentChildMap(allTodosList);
  const todoListForGroup = allTodosList.filter((todo) => {
    return (
      (todo.parent == null || !childrenMap[todo.parent]) &&
      TodoGroupUtils.isInGroup(
        todo,
        groupKey,
        displayMode,
        todoIdMap,
        parentChildMapCache,
      )
    );
  });

  //2. traverse dfs to filter
  const childPassFilterMemo: Record<string, boolean> = {};
  function hasChildPassPositiveFilterCondition(todo: Todo): boolean {
    // returned memoised value if exists
    if (childPassFilterMemo[todo.id] !== undefined) {
      return childPassFilterMemo[todo.id]!;
    }

    const children: Todo[] | undefined = childrenMap[todo.id];

    // if is leaf, self is truth
    const { passPositiveFilter } = filterFn(todo);
    if (!children?.length || passPositiveFilter) {
      childPassFilterMemo[todo.id] = passPositiveFilter;
      return passPositiveFilter;
    }

    // if has children, as long as one child has, return true (recursive)
    for (let child of children) {
      if (hasChildPassPositiveFilterCondition(child)) {
        childPassFilterMemo[todo.id] = true;
        return true;
      }
    }

    // none of children pass
    childPassFilterMemo[todo.id] = false;
    return false;
  }

  //3. traverse dfs to get daily list
  let flattenedTodoList: Todo[] = [];

  let sortedOutermost = TodoUtils.sortedTodos(todoListForGroup);
  //inject indentation
  sortedOutermost = sortedOutermost.map((todo) => ({ ...todo, indent: 0 }));
  let queue = [...sortedOutermost];
  // lowest (closest to root) level that has a todo that passes the filter
  let lowestSelfPassLevel = -1;
  while (queue.length != 0) {
    let todo = queue.shift() as Todo;
    let thisLevel = todo.indent;

    // reset self pass, as it has finished its children
    if (thisLevel <= lowestSelfPassLevel) {
      lowestSelfPassLevel = -1;
    }

    /**
     * in positive filters:
     * - optimistic: if there is at least one in the tree, show result
     * - positive filters trickle down (if parent passes, children pass too)
     *
     * in anti filters: (let's call this negative filters)
     * - pessimistic: if there is at least one in the tree, hide result
     * - negative filters trickle down (if parent fails, children fail too)
     */
    const { passPositiveFilter, passNegativeFilter, hasPositiveFilter } =
      filterFn(todo);
    const hasChildThatPassesPositivefilter =
      hasChildPassPositiveFilterCondition(todo);
    // if there is a postive filter AND a negative filter,
    // but there is a child that fits the positive filter
    // we still want to show that child
    // so, this should only skip if there is no positive filter child matches
    if (
      !passNegativeFilter &&
      !(hasPositiveFilter && hasChildThatPassesPositivefilter)
    ) {
      // skip this todo, and effectively its children
      continue;
    }

    if (passPositiveFilter && lowestSelfPassLevel == -1) {
      lowestSelfPassLevel = thisLevel;
    }

    const parentPassedPositive =
      thisLevel > lowestSelfPassLevel && lowestSelfPassLevel != -1;
    const isRootOfCompleteBranch =
      todo.parent == null && TodoUtils.isBranchComplete(todo.id, todoIdMap); // completed branches should be excluded
    if (isRootOfCompleteBranch && !includeCompleted) {
      // skip this todo, and effectively its children
      continue;
    }

    if (parentPassedPositive || hasChildThatPassesPositivefilter) {
      flattenedTodoList.push(todo);

      if (!todo.collapsed) {
        let children = TodoUtils.sortedTodos(childrenMap[todo.id] || []);
        //inject indentation
        children = children.map((eachTodo) => ({
          ...eachTodo,
          indent: todo.indent + 1,
        }));

        // if self passes, children get free pass
        queue = [...children, ...queue]; //insert children at front - dfs
      }
    }
  }

  return flattenedTodoList;
}

function _groupKeyForDate(
  date: Date | null | Moment | number,
  mode: DisplayMode,
) {
  switch (mode) {
    case "zen":
    case "default": {
      // today | backlog | upcoming | YYYY-MM-DD
      let isSomeday = date == null;
      let isAfterToday = moment(date)
        .startOf("day")
        .isAfter(moment().startOf("day"));

      if (isSomeday || isAfterToday) return PRESET_GROUP_KEYS.UPCOMING;

      let isToday = isSameDay(date, new Date());
      if (isToday) return PRESET_GROUP_KEYS.TODAY;

      let isBeforeToday = moment(date)
        .startOf("day")
        .isBefore(moment().startOf("day"));
      if (isBeforeToday) return PRESET_GROUP_KEYS.BACKLOG;

      return moment(date).startOf("day").format("YYYY-MM-DD");
    }

    case "week": {
      // today | backlog | someday | YYYY-MM-DD
      let isSomeday = date == null;
      if (isSomeday) return PRESET_GROUP_KEYS.SOMEDAY;

      let isToday = isSameDay(date, new Date());
      if (isToday) return PRESET_GROUP_KEYS.TODAY;

      let isBeforeToday = moment(date)
        .startOf("day")
        .isBefore(moment().startOf("day"));
      if (isBeforeToday) return PRESET_GROUP_KEYS.BACKLOG;

      return moment(date).startOf("day").format("YYYY-MM-DD");
    }

    case "calendar": {
      let isSomeday = date == null;
      if (isSomeday) return PRESET_GROUP_KEYS.SOMEDAY;
      return moment(date).startOf("day").format("YYYY-MM-DD");
    }

    default:
      break;
  }

  return moment(date).startOf("day").format("DD MMM, dddd");
}

/**
 * A single todo may belong to multiple groups (i.e. hashtag view)
 * and may be computed based on child properties (i.e. hashtag view)
 */
function groupKeysForTodo(
  todo: Todo,
  displayMode: DisplayMode,
  todoIdMap: TodoIdMap,
  parentChildMapCache: ReturnType<
    typeof TodoUtils.parentChildMap
  > = TodoUtils.parentChildMap(Object.values<Todo>(todoIdMap)),
): GroupKey[] {
  if (isDateBasedGroup(displayMode))
    return [_groupKeyForDate(todo.date, displayMode)];

  if (displayMode === "hashtag") {
    const deepChildrenAndSelf = TodoUtils.deepChildrenOfTodo(
      todoIdMap,
      todo.id,
      parentChildMapCache,
      true,
    );

    const allHashtags = deepChildrenAndSelf
      .map((t) => t.meta?.filter(isHashtagMetaItem).map((h) => h.value) ?? [])
      .flat();

    return Array.from(new Set(allHashtags));
  }

  throw new Error(
    `Unimplemented method to retrieve group keys for todo ${displayMode}`,
  );
}

/**
 * A single todo may belong to multiple groups (i.e. hashtag view)
 * @returns undefined displayMode is not date based
 */
function groupKeyForDate(
  date: Date | null | Moment | number,
  displayMode: DisplayMode,
): GroupKey | undefined {
  if (!isDateBasedGroup(displayMode)) return;

  return _groupKeyForDate(date, displayMode);
}

function groupTitle(groupKey: GroupKey) {
  if (groupKey === PRESET_GROUP_KEYS.TODAY) return "Today";
  if (groupKey === PRESET_GROUP_KEYS.BACKLOG) return "Backlog";
  if (groupKey === PRESET_GROUP_KEYS.UPCOMING) return "Upcoming";
  if (groupKey === PRESET_GROUP_KEYS.SOMEDAY) return "Someday";

  // Check is YYYY-MM-DD
  // If not, return as is
  const isDate = moment(groupKey, "YYYY-MM-DD", true).isValid();
  if (!isDate) {
    return groupKey;
  }

  const isThisWeek =
    moment(groupKey)
      .clone()
      .subtract(7, "days")
      .isSameOrBefore(moment().startOf("day")) || false;
  if (isThisWeek) return moment(groupKey).format("dddd");

  const isThisYear = moment(groupKey).isSame(moment(), "year");
  if (!isThisYear) return moment(groupKey).format("DD MMM YYYY");

  return moment(groupKey).format("DD MMM, ddd");
}

function subGroupTitle(
  todoDate: Date | null | Moment | number,
  displayMode: DisplayMode,
) {
  const date = todoDate ? moment(todoDate).startOf("day") : null;
  if (!date) return "Someday";

  const isThisYear = isSameYear(date.toDate(), new Date());
  const isYesterday = isSameDay(subDays(new Date(), 1), date.toDate());
  const isToday = isSameDay(date, new Date());

  if (!isThisYear) return date.format("DD MMM YYYY");

  if (isYesterday) return "Yesterday";

  switch (displayMode) {
    case "week":
      return date.format("DD MMM");
    case "hashtag":
      if (isToday) return "Today";
  }

  return date.format("ddd, DD MMM");
}

/** @throws if not a date based group */
function dateForGroupKey(key: GroupKey, mode: DisplayMode): number | null {
  if (!isDateBasedGroup(mode))
    throw new Error(
      `Attempted to get date for key ${key} in non-date mode ${mode}`,
    );

  let weekDayDates: Moment[] = [];
  for (let i = 1; i < WEEK_FORECAST_DAYS; i++) {
    weekDayDates.push(moment().add(i, "days").startOf("day"));
  }

  if (key == PRESET_GROUP_KEYS.TODAY) {
    return moment().startOf("day").valueOf();
  } else if (key === PRESET_GROUP_KEYS.BACKLOG) {
    return moment().startOf("day").subtract(1, "day").valueOf();
  } else if (key === PRESET_GROUP_KEYS.SOMEDAY) {
    return null;
  } else if (key === PRESET_GROUP_KEYS.UPCOMING) {
    return moment().startOf("day").add(1, "day").valueOf();
  } else if (weekDayDates.map((d) => _groupKeyForDate(d, mode)).includes(key)) {
    return (
      weekDayDates
        .find((d) => _groupKeyForDate(d, mode) == key)
        ?.startOf("day")
        .valueOf() ?? null
    );
  } else if (moment(key)) {
    return moment(key).valueOf();
  }

  throw new Error(`No date for group key ${key}`);
}

function applyGroupKeyToTodo(
  todo: Todo,
  groupKey: GroupKey,
  displayMode: DisplayMode,
): TodoUpdateDelta {
  if (isDateBasedGroup(displayMode)) {
    const date = dateForGroupKey(groupKey, displayMode);
    return {
      id: todo.id,
      date: date,
    };
  }

  if (displayMode === "hashtag") {
    let didChange = false;
    let meta = [...(todo.meta ?? [])]; // Make sure different array reference for store updates
    // remove current hashtag from toods
    // meta = meta.filter((m) => {
    //   if (isHashtagMetaItem(m) && m.value === currentGroupKey) {
    //     didChange = true;
    //     return false;
    //   }
    //   return true;
    // });
    if (!meta.find((h) => h.value === groupKey)) {
      didChange = true;
      meta.push({ metaType: "HASHTAG", value: groupKey });
    }

    return {
      id: todo.id,
      ...(didChange && { meta }),
    };
  }

  throw new Error(
    `Unimplemented method to retrieve group keys for todo ${displayMode}`,
  );
}

function isInGroup(
  todo: Todo,
  groupKey: GroupKey,
  displayMode: DisplayMode,
  todoIdMap: TodoIdMap,
  parentChildMapCache: ReturnType<
    typeof TodoUtils.parentChildMap
  > = TodoUtils.parentChildMap(Object.values<Todo>(todoIdMap)),
) {
  return TodoGroupUtils.groupKeysForTodo(
    todo,
    displayMode,
    todoIdMap,
    parentChildMapCache,
  ).includes(groupKey);
}

/**
 * Returns a 2d grid of groupKeys (YYYY-MM-DD) for a given reference date
 * within a date window. The grid should span the first day of the week, of
 * the first day of the month, of the month of the reference date - to the
 * last day of the week, of the last day of the month.
 */
function getCalendarGroupKeyGrid(
  referenceDate: Date,
  firstDayInWeek: "monday" | "sunday",
) {
  const startOfMonthDate = startOfMonth(referenceDate);
  const endOfMonthDate = endOfMonth(referenceDate);
  const firstDayOfFirstWeek = startOfWeek(startOfMonthDate, {
    weekStartsOn: firstDayInWeek === "monday" ? 1 : 0,
  });
  const lastDayOfLastWeek = startOfDay(endOfWeek(endOfMonthDate));
  const grid: GroupKey[][] = [];
  let currentDate = firstDayOfFirstWeek;
  while (currentDate <= lastDayOfLastWeek) {
    if (!grid[grid.length - 1] || grid[grid.length - 1]?.length === 7) {
      grid.push([]);
    }

    const groupKey = groupKeyForDate(currentDate, "calendar");
    if (!groupKey)
      throw new Error(`Could not create group key for date ${currentDate}`);
    grid[grid.length - 1]?.push(groupKey);
    currentDate = addDays(currentDate, 1);
  }

  return grid;
}

export const TodoGroupUtils = {
  getCalendarGroupKeyGrid,
  dateForGroupKey,
  isInGroup,
  groupKeysForTodo,
  groupKeyForDate,
  groupTitle,
  subGroupTitle,
  applyGroupKeyToTodo,
  todoListForGroup,
  todoListByGroup,
};

export default TodoGroupUtils;
